/**
 * ControlSurfacePlanform — the left-pane schematic of the Control Surfaces
 * workbench: every segmented lifting surface drawn in the (span, x) plane —
 * x vs y for span_axis 'y' surfaces (wing, tails), x vs z for the fin — with
 * the active control-surface span band shaded and the hinge line drawn from
 * the engine's hinge x at the band edges.
 *
 * Surfaces whose spanwise sections have arrived draw their TRUE planform
 * (LE/TE curves per station — taper, sweep, tips); surfaces still slicing
 * fall back to a dashed bbox rectangle. Pure SVG; 3D overlay is a follow-up.
 */
import type { SegSurface } from "../../api/controlSurface";
import type { HighliftPalette } from "../highlift/foil/useHighliftTheme";

const W = 760;
const H = 520;
const PAD = 36;

/** [min, max] of the surface's span coordinate (y for planar, z for the fin). */
function spanExtent(s: SegSurface): [number, number] {
  return s.span_axis === "y" ? [s.bbox[1], s.bbox[4]] : [s.bbox[2], s.bbox[5]];
}

/** [min, max] of the surface's chordwise (x) coordinate. */
function chordExtent(s: SegSurface): [number, number] {
  return [s.bbox[0], s.bbox[3]];
}

/** True when the bbox covers both semispans (only meaningful for span_axis 'y'). */
export function spansBothSides(s: SegSurface): boolean {
  const [s0, s1] = spanExtent(s);
  return s.span_axis === "y" && s0 < 0 && s1 > 0;
}

/** Root/tip of the eta axis: eta runs across the FULL bbox span (lo..hi) — the same
 *  convention the engine, /sections and bake use, including for mirrored bboxes. */
function etaAxis(s: SegSurface): [number, number] {
  return spanExtent(s);
}

export interface ControlSurfacePlanformProps {
  palette: HighliftPalette;
  surfaces: SegSurface[];
  active: SegSurface | null;
  eta1: number;
  eta2: number;
  mirror: boolean;
  /** Engine hinge x at the band edges (eta1, eta2) in model coords, when computable. */
  hingeEdges: { x1: number; x2: number } | null;
  /** TRUE planform outlines per surfaceKey: sorted [span, x_le, x_te] stations from the
   *  sections slicer. Surfaces without one fall back to their bbox rectangle. */
  outlines?: Record<string, Array<[number, number, number]>>;
  /**
   * Aircraft z-orientation from the segmenter (+1 z-up, -1 z-down). DISPLAY-ONLY: when -1 the
   * DRAWN z axis of span_axis 'z' surfaces (the fin pane) is flipped so fins draw pointing
   * UPWARD (toward +span on the schematic); eta, the engine and the bake stay in model coords.
   */
  upSign?: 1 | -1;
  onPick: (surface: SegSurface) => void;
}

/** Closed SVG paths (LE out, TE back) for one outline, split where consecutive stations
 *  jump more than 3x the median spacing - a mirrored wing's fuselage carry-through has
 *  no clean sections, and lerping across it would draw wing skin through the cabin. */
function outlinePaths(
  pts: Array<[number, number, number]>,
  X: (h: number) => number,
  Y: (x: number) => number,
): string[] {
  const first = pts[0];
  if (pts.length < 2 || !first) return [];
  const gaps: number[] = [];
  for (let i = 1; i < pts.length; i++) gaps.push(pts[i]![0] - pts[i - 1]![0]);
  gaps.sort((a, b) => a - b);
  const med = gaps[Math.floor(gaps.length / 2)] || 1;
  const runs: Array<Array<[number, number, number]>> = [[first]];
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i]!;
    if (p[0] - pts[i - 1]![0] > 3 * med) runs.push([]);
    runs[runs.length - 1]!.push(p);
  }
  return runs
    .filter((r) => r.length >= 2)
    .map((r) => {
      const le = r.map((p) => `${X(p[0]).toFixed(1)},${Y(p[1]).toFixed(1)}`);
      const te = [...r].reverse().map((p) => `${X(p[0]).toFixed(1)},${Y(p[2]).toFixed(1)}`);
      return `M ${le.join(" L ")} L ${te.join(" L ")} Z`;
    });
}

/** LE/TE of an outline linearly interpolated at one span station (outline sorted by span). */
function outlineAt(
  pts: Array<[number, number, number]>,
  s: number,
): [number, number] | null {
  if (pts.length < 2) return null;
  const first = pts[0]!;
  const last = pts[pts.length - 1]!;
  if (s <= first[0]) return [first[1], first[2]];
  if (s >= last[0]) return [last[1], last[2]];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    if (s >= a[0] && s <= b[0]) {
      const f = (s - a[0]) / (b[0] - a[0] || 1);
      return [a[1] + f * (b[1] - a[1]), a[2] + f * (b[2] - a[2])];
    }
  }
  return null;
}

/** Closed band path CLIPPED to the true outline between span stations s0..s1 - a tapered
 *  or swept wing's CS band must follow its own LE/TE, not the bbox chord rectangle. */
function bandPath(
  pts: Array<[number, number, number]>,
  s0: number,
  s1: number,
  X: (h: number) => number,
  Y: (x: number) => number,
): string | null {
  const e0 = outlineAt(pts, s0);
  const e1 = outlineAt(pts, s1);
  if (!e0 || !e1) return null;
  const interior = pts.filter((p) => p[0] > s0 && p[0] < s1);
  const le = [[s0, e0[0]] as [number, number]]
    .concat(interior.map((p) => [p[0], p[1]] as [number, number]))
    .concat([[s1, e1[0]]]);
  const te = [[s1, e1[1]] as [number, number]]
    .concat([...interior].reverse().map((p) => [p[0], p[2]] as [number, number]))
    .concat([[s0, e0[1]]]);
  const seg = (pt: [number, number]) => `${X(pt[0]).toFixed(1)},${Y(pt[1]).toFixed(1)}`;
  return `M ${le.map(seg).join(" L ")} L ${te.map(seg).join(" L ")} Z`;
}

export function ControlSurfacePlanform({
  palette,
  surfaces,
  active,
  eta1,
  eta2,
  mirror,
  hingeEdges,
  outlines,
  upSign = 1,
  onPick,
}: ControlSurfacePlanformProps) {
  if (surfaces.length === 0) return null;

  // DISPLAY-ONLY z flip for the fin pane on a z-DOWN aircraft (upSign -1): span_axis 'z' values
  // are negated at DRAW time so the fin points UP the schematic's span axis. Wing/tail surfaces
  // (span_axis 'y') are untouched, and eta / the engine hinge x stay in model terms throughout.
  const flipFin = upSign === -1;
  const drawSpan = (s: SegSurface, v: number): number =>
    flipFin && s.span_axis === "z" ? -v : v;
  /** Drawn [min, max] span extent of a surface (ordering restored after any flip). */
  const drawSpanExtent = (s: SegSurface): [number, number] => {
    const [a, b] = spanExtent(s);
    const p = drawSpan(s, a);
    const q = drawSpan(s, b);
    return p <= q ? [p, q] : [q, p];
  };
  /** A surface's outline in DRAW space (span flipped + re-sorted ascending for the fin pane). */
  const outlineFor = (key: string, s: SegSurface): Array<[number, number, number]> | undefined => {
    const raw = outlines?.[key];
    if (!raw || !(flipFin && s.span_axis === "z")) return raw;
    return raw
      .map((p) => [-p[0], p[1], p[2]] as [number, number, number])
      .sort((a, b) => a[0] - b[0]);
  };

  // Global extents across every surface, in the (span, x) schematic plane.
  let h0 = Infinity;
  let h1 = -Infinity;
  let v0 = Infinity;
  let v1 = -Infinity;
  for (const s of surfaces) {
    const [a, b] = drawSpanExtent(s);
    const [c, d] = chordExtent(s);
    if (a < h0) h0 = a;
    if (b > h1) h1 = b;
    if (c < v0) v0 = c;
    if (d > v1) v1 = d;
  }
  const dh = Math.max(h1 - h0, 1e-9);
  const dv = Math.max(v1 - v0, 1e-9);
  const scale = Math.min((W - 2 * PAD) / dh, (H - 2 * PAD) / dv);
  const offH = (W - dh * scale) / 2;
  const offV = (H - dv * scale) / 2;
  const X = (h: number) => offH + (h - h0) * scale;
  // x aft points DOWN the schematic (leading edges up), the planform convention.
  const Y = (x: number) => offV + (x - v0) * scale;

  const activeKey = active ? `${active.label}:${active.bbox.join(",")}` : null;

  // Active band geometry (span positions of eta1/eta2 along the surface's full-span eta
  // axis). The mirror band is the same eta band reflected about the centreline, which on
  // the SAME axis is simply 1-eta2..1-eta1.
  // h1/h2 are the DRAWN span positions of the eta1/eta2 band edges (they pair with the engine
  // hinge x1/x2); hA/hB re-order them for the rect / band path, because the fin-pane flip
  // reverses the drawn axis (h1 can land right of h2). Mirror positions stay raw: the mirror
  // band only ever draws for span_axis 'y' surfaces, where drawSpan is the identity.
  let band: { h1: number; h2: number; hA: number; hB: number; mA: number; mB: number; xTop: number; xBot: number } | null = null;
  if (active) {
    const [lo, hi] = etaAxis(active);
    const at = (eta: number) => lo + eta * (hi - lo);
    const e1 = Math.min(eta1, eta2);
    const e2 = Math.max(eta1, eta2);
    const [c0, c1] = chordExtent(active);
    const p1 = drawSpan(active, at(e1));
    const p2 = drawSpan(active, at(e2));
    band = {
      h1: p1,
      h2: p2,
      hA: Math.min(p1, p2),
      hB: Math.max(p1, p2),
      mA: at(1 - e2),
      mB: at(1 - e1),
      xTop: c0,
      xBot: c1,
    };
  }
  const showMirror = Boolean(active && mirror && spansBothSides(active));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Aircraft planform schematic with segmented lifting surfaces"
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      {surfaces.map((s) => {
        const [a, b] = drawSpanExtent(s);
        const [c, d] = chordExtent(s);
        const key = `${s.label}:${s.bbox.join(",")}`;
        const isActive = key === activeKey;
        const outline = outlineFor(key, s);
        const paths = outline ? outlinePaths(outline, X, Y) : [];
        return (
          <g key={key} onClick={() => onPick(s)} style={{ cursor: "pointer" }}>
            {/* click target across the whole extent, always */}
            <rect
              x={X(a)}
              y={Y(c)}
              width={(b - a) * scale}
              height={(d - c) * scale}
              fill="transparent"
              fillOpacity={0}
              stroke={paths.length > 0 ? "none" : isActive ? palette.primary : palette.lineStrong}
              strokeWidth={isActive ? 1.8 : 1.2}
              strokeDasharray={paths.length > 0 ? undefined : "5 4"}
              rx={3}
            />
            {paths.length === 0 && isActive ? (
              <rect
                x={X(a)}
                y={Y(c)}
                width={(b - a) * scale}
                height={(d - c) * scale}
                fill={palette.primarySoft}
                fillOpacity={0.7}
                stroke="none"
                rx={3}
              />
            ) : null}
            {paths.map((p, i) => (
              <path
                key={i}
                d={p}
                fill={isActive ? palette.primarySoft : "transparent"}
                fillOpacity={isActive ? 0.7 : 0}
                stroke={isActive ? palette.primary : palette.lineStrong}
                strokeWidth={isActive ? 1.8 : 1.2}
                strokeLinejoin="round"
              />
            ))}
            <text
              x={X(a) + 5}
              y={Y(c) + 13}
              fontSize={11}
              fill={isActive ? palette.primary : palette.ink2}
              style={{ fontFamily: "var(--v5-font-mono, monospace)", userSelect: "none" }}
            >
              {s.label}
              {s.span_axis === "z" ? " (fin)" : ""}
            </text>
            <text
              x={X(a) + 5}
              y={Y(c) + 25}
              fontSize={9}
              fill={palette.ink4}
              style={{ fontFamily: "var(--v5-font-mono, monospace)", userSelect: "none" }}
            >
              span {(b - a).toFixed(1)} x chord {(d - c).toFixed(1)}
            </text>
          </g>
        );
      })}

      {active && band ? (
        <g pointerEvents="none">
          {(() => {
            const outline = outlineFor(activeKey ?? "", active);
            const main = outline ? bandPath(outline, band.hA, band.hB, X, Y) : null;
            const mir =
              showMirror && outline ? bandPath(outline, band.mA, band.mB, X, Y) : null;
            return (
              <>
                {main ? (
                  <path d={main} fill={palette.amber} fillOpacity={0.22} stroke={palette.amber} strokeWidth={1} strokeLinejoin="round" />
                ) : (
                  <rect
                    x={X(band.hA)}
                    y={Y(band.xTop)}
                    width={(band.hB - band.hA) * scale}
                    height={(band.xBot - band.xTop) * scale}
                    fill={palette.amber}
                    fillOpacity={0.22}
                    stroke={palette.amber}
                    strokeWidth={1}
                  />
                )}
                {showMirror ? (
                  mir ? (
                    <path d={mir} fill={palette.amber} fillOpacity={0.12} stroke={palette.amber} strokeWidth={1} strokeDasharray="4 3" strokeLinejoin="round" />
                  ) : (
                    <rect
                      x={X(band.mA)}
                      y={Y(band.xTop)}
                      width={(band.mB - band.mA) * scale}
                      height={(band.xBot - band.xTop) * scale}
                      fill={palette.amber}
                      fillOpacity={0.12}
                      stroke={palette.amber}
                      strokeWidth={1}
                      strokeDasharray="4 3"
                    />
                  )
                ) : null}
              </>
            );
          })()}
          {hingeEdges ? (
            <>
              {/* hinge x1 belongs to the eta1 edge (h1) and x2 to eta2 (h2) — drawn positions,
                  so the pairing survives the fin-pane flip. */}
              <line
                x1={X(band.h1)}
                y1={Y(hingeEdges.x1)}
                x2={X(band.h2)}
                y2={Y(hingeEdges.x2)}
                stroke={palette.flaperonHinge}
                strokeWidth={1.8}
                strokeDasharray="6 3"
              />
              {showMirror ? (
                // eta1 reflects to 1-eta1 (= mB), eta2 to 1-eta2 (= mA) — hinge x follows its eta.
                <line
                  x1={X(band.mB)}
                  y1={Y(hingeEdges.x1)}
                  x2={X(band.mA)}
                  y2={Y(hingeEdges.x2)}
                  stroke={palette.flaperonHinge}
                  strokeWidth={1.2}
                  strokeDasharray="3 3"
                />
              ) : null}
            </>
          ) : null}
        </g>
      ) : null}

      <text
        x={W - PAD}
        y={H - 10}
        fontSize={9}
        textAnchor="end"
        fill={palette.ink4}
        style={{ fontFamily: "var(--v5-font-mono, monospace)", userSelect: "none" }}
      >
        span across - x aft, down · band = CS extent · dashed = hinge
      </text>
    </svg>
  );
}
